/**
 * Silver Falls Capital Theme Configuration
 * Pacific Northwest inspired color palette
 */

import { ThemeConfig } from '../types';

export const silverFallsCapitalTheme: ThemeConfig = {
  id: 'silver-falls-capital',
  name: 'Silver Falls Capital',
  description: 'Pacific Northwest inspired color palette with sophisticated greys and ocean tones',
  
  // Brand colors
  colors: {
    // Primary Colors - First choice for design elements
    richBlack: '#04131C',
    mediumGrey: '#ACB6BC',
    white: '#FFFFFF',
    
    // Secondary Colors - Accent colors
    deepOcean: '#223F47',
    mist: '#E7EEF2',
    darkSky: '#122630',
    stoneGrey: '#708087',
    
    // Tertiary Colors - Secondary choice for accents
    darkGrey: '#394851',
    ocean: '#266970',
    lightGrey: '#C5CED3',
    
    // Action Colors - For warnings and confirmations
    warningRed: '#FF2020',
    warningRedHover: '#E51C1C',
    warningRedActive: '#CC1818',
    successGreen: '#92D050',
    successGreenHover: '#7BB83A',
    successGreenActive: '#6BA030',
  },

  // CSS Variables mapping
  cssVariables: {
    // Typography
    '--font-family-primary': '"Manrope", system-ui, sans-serif',
    '--font-family-mono': '"Manrope", monospace',
    
    // Brand colors
    '--sfc-rich-black': '#04131C',
    '--sfc-medium-grey': '#ACB6BC',
    '--sfc-white': '#FFFFFF',
    '--sfc-deep-ocean': '#223F47',
    '--sfc-mist': '#E7EEF2',
    '--sfc-dark-sky': '#122630',
    '--sfc-stone-grey': '#708087',
    '--sfc-dark-grey': '#394851',
    '--sfc-ocean': '#266970',
    '--sfc-light-grey': '#C5CED3',
    '--sfc-warning-red': '#FF2020',
    '--sfc-warning-red-hover': '#E51C1C',
    '--sfc-warning-red-active': '#CC1818',
    '--sfc-success-green': '#92D050',
    '--sfc-success-green-hover': '#7BB83A',
    '--sfc-success-green-active': '#6BA030',
    
    // Semantic color mapping
    '--color-primary': '#708087',
    '--color-primary-hover': '#394851',
    '--color-primary-active': '#223F47',
    '--color-primary-light': '#E7EEF2',
    
    '--color-success': '#92D050',
    '--color-success-hover': '#7BB83A',
    '--color-success-active': '#6BA030',
    '--color-success-light': '#E7EEF2',
    
    '--color-warning': '#FF2020',
    '--color-warning-hover': '#E51C1C',
    '--color-warning-active': '#CC1818',
    '--color-warning-light': '#C5CED3',
    
    '--color-error': '#FF2020',
    '--color-error-hover': '#E51C1C',
    '--color-error-active': '#CC1818',
    '--color-error-light': '#E7EEF2',
    
    // Text colors
    '--color-text-primary': '#04131C',
    '--color-text-secondary': '#394851',
    '--color-text-tertiary': '#708087',
    '--color-text-disabled': '#ACB6BC',
    '--color-text-inverse': '#FFFFFF',
    
    // Background colors
    '--color-bg-primary': '#FFFFFF',
    '--color-bg-secondary': '#E7EEF2',
    '--color-bg-tertiary': '#C5CED3',
    '--color-bg-disabled': '#C5CED3',
    '--color-bg-inverse': '#04131C',
    
    // Border colors
    '--color-border-primary': '#ACB6BC',
    '--color-border-secondary': '#C5CED3',
    '--color-border-hover': '#708087',
    '--color-border-focus': '#708087',
    
    // Shadow colors
    '--shadow-light': '0 2px 8px rgba(4, 19, 28, 0.08)',
    '--shadow-medium': '0 4px 12px rgba(4, 19, 28, 0.12)',
    '--shadow-heavy': '0 6px 16px rgba(4, 19, 28, 0.16)',
    '--shadow-popover': '0 2px 8px rgba(4, 19, 28, 0.08)',
    
    // Overlay colors
    '--color-overlay-dark': 'rgba(4, 19, 28, 0.5)',
    '--color-overlay-light': 'rgba(231, 238, 242, 0.8)',
    
    // Layout colors
    '--color-layout-header': '#FFFFFF',
    '--color-layout-sidebar': '#E7EEF2',
    '--color-layout-content': '#FFFFFF',
    
    // Component specific colors
    '--color-card-bg': '#FFFFFF',
    '--color-card-border': '#C5CED3',
    '--color-card-shadow': '0 2px 8px rgba(4, 19, 28, 0.08)',
    
    '--color-input-bg': '#FFFFFF',
    '--color-input-border': '#ACB6BC',
    '--color-input-hover': '#708087',
    '--color-input-focus': '#708087',
    
    '--color-button-default-bg': '#FFFFFF',
    '--color-button-default-border': '#ACB6BC',
    '--color-button-default-hover': '#E7EEF2',
    
    // Tag colors
    '--color-tag-primary': '#708087',
    '--color-tag-secondary': '#266970',
    '--color-tag-tertiary': '#223F47',
    '--color-tag-muted': '#ACB6BC',
    '--color-tag-dark': '#394851',
    
    // Interaction states
    '--color-hover-bg': 'rgba(112, 128, 135, 0.05)',
    '--color-active-bg': 'rgba(112, 128, 135, 0.10)',
    '--color-selected-bg': 'rgba(112, 128, 135, 0.15)',
    '--color-focus-outline': '#708087',
    
    // Workspace specific
    '--color-workspace-bg': '#E7EEF2',
    '--color-workspace-sidebar': '#FFFFFF',
    '--color-workspace-border': '#C5CED3',
    
    // Chat specific
    '--color-chat-bg': '#FFFFFF',
    '--color-chat-message-bg': '#FFFFFF',
    '--color-chat-message-border': '#C5CED3',
    '--color-chat-message-shadow': '0 2px 8px rgba(4, 19, 28, 0.08)',
    '--color-chat-user-badge': '#708087',
    '--color-chat-assistant-badge': '#266970',
    
    // Navigation
    '--color-nav-bg': '#FFFFFF',
    '--color-nav-item-hover': '#E7EEF2',
    '--color-nav-item-active': 'rgba(112, 128, 135, 0.10)',
    '--color-nav-text': '#04131C',
    '--color-nav-text-secondary': '#394851',
    
    // Status colors
    '--color-status-online': '#266970',
    '--color-status-offline': '#122630',
    '--color-status-warning': '#708087',
    '--color-status-info': '#223F47',
    
    // Email integration
    '--color-email-unread': '#708087',
    '--color-email-read-opacity': '0.6',
    
    // MCP server colors
    '--color-mcp-connected': '#266970',
    '--color-mcp-disconnected': '#122630',
    '--color-mcp-category-bg': '#E7EEF2'
  },

  // Dark theme variants
  darkMode: {
    '--color-text-primary': '#FFFFFF',
    '--color-text-secondary': '#C5CED3',
    '--color-text-tertiary': '#ACB6BC',
    '--color-text-disabled': '#708087',
    '--color-text-inverse': '#04131C',
    
    '--color-bg-primary': '#04131C',
    '--color-bg-secondary': '#122630',
    '--color-bg-tertiary': '#394851',
    '--color-bg-disabled': '#394851',
    '--color-bg-inverse': '#FFFFFF',
    
    '--color-border-primary': '#394851',
    '--color-border-secondary': '#708087',
    
    '--color-card-bg': '#122630',
    '--color-card-border': '#394851',
    
    '--color-workspace-bg': '#04131C',
    '--color-workspace-sidebar': '#122630',
    '--color-workspace-border': '#394851',
    
    '--color-chat-bg': '#04131C',
    '--color-chat-message-bg': '#122630',
    '--color-chat-message-border': '#394851',
    
    '--color-nav-bg': '#122630',
    '--color-nav-item-hover': '#394851',
    '--color-nav-item-active': 'rgba(112, 128, 135, 0.20)',
    '--color-nav-text': '#FFFFFF',
    '--color-nav-text-secondary': '#C5CED3',
    
    '--color-hover-bg': 'rgba(112, 128, 135, 0.15)',
    '--color-active-bg': 'rgba(112, 128, 135, 0.25)',
    '--color-selected-bg': 'rgba(112, 128, 135, 0.30)',
    
    '--color-mcp-category-bg': '#394851'
  },

  // Ant Design theme configuration
  antTheme: {
    token: {
      colorPrimary: '#708087',
      colorPrimaryHover: '#394851',
      colorPrimaryActive: '#223F47',
      colorSuccess: '#92D050',
      colorWarning: '#FF2020',
      colorError: '#FF2020',
      colorInfo: '#223F47',
      colorBgContainer: '#FFFFFF',
      colorBgElevated: '#E7EEF2',
      colorText: '#04131C',
      colorTextSecondary: '#394851',
      colorBorder: '#C5CED3',
      fontFamily: '"Manrope", system-ui, sans-serif',
      borderRadius: 8
    },
    components: {
      Button: {
        primaryShadow: 'none',
        defaultShadow: 'none',
        fontWeight: 500
      },
      Card: {
        boxShadowTertiary: '0 2px 8px rgba(4, 19, 28, 0.08)',
        headerBg: '#E7EEF2'
      },
      Input: {
        activeBorderColor: '#708087',
        hoverBorderColor: '#708087',
        activeShadow: 'none'
      }
    }
  }
};

export default silverFallsCapitalTheme;